// DispatchPDF.jsx
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFViewer,
} from "@react-pdf/renderer";
import { useLocation } from "react-router-dom";

const styles = StyleSheet.create({
  page: {
    padding: "20 30",
    fontSize: 12,
    fontFamily: "Helvetica",
    // stack vertically: 3 rows per page
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  container: {
    border: "1px",
    borderColor: "#000",
    flex: 1,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "5 10",
    // borderBottom: 1,
    borderColor: "#000",
  },
  title: {
    textAlign: "center",
    padding: "5 0",
    borderBottom: 1,
    borderColor: "#000",
    fontWeight: "bold",
  },
  table: {
    borderTop: "1px",
    borderColor: "#000",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottom: 1,
    borderColor: "#000",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCell: {
    padding: 5,
    flex: 1,
    borderRight: 1,
    borderColor: "#000",
  },
  tableCellLast: {
    padding: 5,
    flex: 1,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "40 10 10 10",
  },
  contentArea: {
    minHeight: 300,
  },
  // wrapper for each of the 3 rows on a page
  rowSlot: {
    // give each row equal height so 3 rows fit vertically
    flex: 1,
    marginBottom: 8,
    // row contains two copies side-by-side
    display: "flex",
    flexDirection: "row",
    gap: 6,
  },
  // wrapper around each copy (keeps original Report container styling intact)
  copyWrapper: {
    flex: 1,
  },
  // blank slot (for an entire row when item missing) - nothing inside, no border
  blankRow: {
    flex: 1,
  },
});

// date formatter (defensive)
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, '0'); // Adds leading zero if single digit
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Adds leading zero if single digit
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const Report = ({ data }) => {
  // guard against undefined fields
  const vno = data?.vno ?? "";
  const DateVal = data?.Date ?? data?.DateString ?? "";
  const cardNo = data?.cardNo ?? data?.cardno ?? "";
  const partyName = data?.partyName ?? data?.party ?? "";
  const qty = data?.qty ?? "";
  const description = data?.description ?? data?.drgNo ?? data?.drgno ?? "";

  return (
    <View style={styles.container}>
      {/* Header Info */}
      <View style={styles.headerRow}>
        <Text>Sr.No. {vno}</Text>
        <Text>Date {formatDate(DateVal)}</Text>
      </View>
      <View style={styles.headerRow}>
        <Text>Card No. {cardNo}</Text>
      </View>

      {/* Title */}
      <View style={styles.title}>
        <Text style={{ fontFamily: "Helvetica-Bold" }}>{partyName}</Text>
      </View>
      {/* Table */}
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableCell}>Qty.(Pcs.)</Text>
          <Text style={styles.tableCellLast}>Description</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCellLast}>{qty}</Text>
          <Text style={styles.tableCellLast}>{description}</Text>
        </View>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text>Party Sign.</Text>
        <Text>Signature</Text>
      </View>
    </View>
  );
};

// chunk array into groups of 'size'
const chunkArray = (arr, size) => {
  const res = [];
  for (let i = 0; i < arr.length; i += size) {
    res.push(arr.slice(i, i + size));
  }
  return res;
};

const PDFContent = ({ bulk }) => {
  const items = Array.isArray(bulk) ? bulk : (bulk ? [bulk] : []);
  // we want 3 voucher-rows per page
  const pages = chunkArray(items, 3);

  return (
    <Document>
      {pages.length > 0 ? (
        pages.map((chunk, pageIdx) => (
          <Page key={pageIdx} size="A4" style={styles.page}>
            {/*
              For each of the 3 row slots on the page:
                - if chunk has an item at that index, render a row with two side-by-side copies of that Report
                - if missing, render a blank row (no borders)
            */}
            {Array.from({ length: 3 }).map((_, rowIdx) => {
              const item = chunk[rowIdx];
              return (
                <View key={rowIdx} style={styles.rowSlot}>
                  {item ? (
                    <>
                      <View style={styles.copyWrapper}>
                        <Report data={item} />
                      </View>
                      <View style={styles.copyWrapper}>
                        <Report data={item} />
                      </View>
                    </>
                  ) : (
                    // blank entire row (no borders, just empty space)
                    <View style={styles.blankRow} />
                  )}
                </View>
              );
            })}
          </Page>
        ))
      ) : (
        <Page size="A4" style={styles.page}>
          <Text>No data</Text>
        </Page>
      )}
    </Document>
  );
};

const DispatchPDF = () => {
  const location = useLocation();
  const state = location.state || {};
  // support single object (state) or bulk array (state.bulk)
  const bulk = Array.isArray(state?.bulk) ? state.bulk : (state ? [state] : []);
  return (
    <div style={{ width: "100%", height: "600px" }}>
      <PDFViewer width="100%" height="100%">
        <PDFContent bulk={bulk} />
      </PDFViewer>
    </div>
  );
};

export default DispatchPDF;
